/*
 * SK GA4 / GTM dataLayer helper.
 *
 * Central place for the GA4 ecommerce events that this theme fires from the
 * storefront. Because `enable_quotation` replaces Shopify's native checkout,
 * the standard checkout funnel (begin_checkout / purchase) never runs, so the
 * theme pushes its own events to `window.dataLayer` following the GA4 + GTM
 * convention: clear the previous `ecommerce` object, then push the event.
 *
 * Events fired here (JS-driven):
 *   - add_to_cart      — diffed from /cart.js on the `ajaxProduct:added` signal
 *   - remove_from_cart — diffed from the `cart:update` event detail
 *   - view_cart        — when the cart drawer opens
 *   - search           — see sk-machine-search-results.js (calls SKAnalytics.search)
 *   - generate_lead    — see sk-contact-form.js (calls SKAnalytics.generateLead)
 *   - view_item_list /
 *     select_item      — machine-search results (JS) + collection grid (Liquid)
 *   - purchase         — quote submitted (assets/quote.js delegates here)
 *
 * Page-load events fired directly from Liquid snippets (self-contained, no
 * dependency on this file): view_item, view_item_list, view_cart on the cart
 * page — see snippets/sk-ga4-*.liquid.
 */
(function () {
  'use strict';

  function dataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  // GA4 + GTM convention: null out the previous ecommerce object first so
  // values from an earlier event never leak into this one.
  function push(eventName, ecommerce) {
    try {
      var dl = dataLayer();
      dl.push({ ecommerce: null });
      dl.push({ event: eventName, ecommerce: ecommerce });
    } catch (e) { /* analytics must never break the page */ }
  }

  function money(cents) {
    return Math.round((cents || 0)) / 100;
  }

  // Map a Shopify /cart.js line item to a GA4 item object.
  function itemFromCartLine(item, index) {
    var out = {
      // Prefer SKU, then the variant id so different variants stay distinct,
      // and only fall back to the product id when neither is available.
      item_id: item.sku || String(item.variant_id || item.product_id),
      item_name: item.product_title || item.title,
      price: money(item.price),
      quantity: item.quantity,
      index: index
    };
    if (item.variant_title) out.item_variant = item.variant_title;
    if (item.vendor) out.item_brand = item.vendor;
    if (item.product_type) out.item_category = item.product_type;
    return out;
  }

  function sumValue(items) {
    return items.reduce(function (total, it) {
      return total + (it.price || 0) * (it.quantity || 1);
    }, 0);
  }

  /* ---------------------------------------------------------------------- *
   * add_to_cart / remove_from_cart — cart state diffing                    *
   *                                                                        *
   * The theme's three add-to-cart call sites all dispatch a payload-less   *
   * `ajaxProduct:added` event, and cart mutations dispatch `cart:update`.  *
   * We keep a snapshot of the cart keyed by line key and diff against it   *
   * to work out what changed and in which direction.                       *
   * ---------------------------------------------------------------------- */
  var cartSnapshot = null; // { <lineKey>: { quantity, item, index } }

  // Every cart refresh runs through this single promise chain so that
  // overlapping /cart.js fetches (e.g. the initial seed racing a fast
  // add-to-cart) can't resolve out of order and diff against a stale or null
  // snapshot, which would inflate add_to_cart / remove_from_cart payloads.
  var cartQueue = Promise.resolve();
  function serializeCartOp(task) {
    // task callbacks never reject (each ends in .catch), so the chain is safe.
    cartQueue = cartQueue.then(function () { return task(); });
    return cartQueue;
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function snapshotFromCart(cart) {
    var map = {};
    (cart.items || []).forEach(function (item, index) {
      map[item.key] = { quantity: item.quantity, item: item, index: index };
    });
    return map;
  }

  function seedCart() {
    return serializeCartOp(function () {
      return fetchCart()
        .then(function (cart) { cartSnapshot = snapshotFromCart(cart); })
        .catch(function () { /* leave snapshot as-is */ });
    });
  }

  function handleAdded() {
    serializeCartOp(function () {
      return fetchCart()
        .then(function (cart) {
          var prev = cartSnapshot || {};
          var added = [];
          (cart.items || []).forEach(function (item, index) {
            var before = prev[item.key] ? prev[item.key].quantity : 0;
            var delta = item.quantity - before;
            if (delta > 0) {
              var ga4 = itemFromCartLine(item, index);
              ga4.quantity = delta;
              added.push(ga4);
            }
          });
          cartSnapshot = snapshotFromCart(cart);
          if (added.length) {
            push('add_to_cart', {
              currency: cart.currency,
              value: sumValue(added),
              items: added
            });
          }
        })
        .catch(function () {});
    });
  }

  function handleCartUpdate(e) {
    var cart = e && e.detail;
    serializeCartOp(function () {
      // A real Shopify cart response carries `token`. The synthetic detail that
      // quote.js dispatches after clearing the cart on a successful quote does
      // not — that basket became a `purchase`, not a removal, so just resync.
      if (!cart || !Array.isArray(cart.items) || !('token' in cart)) {
        return fetchCart()
          .then(function (fresh) { cartSnapshot = snapshotFromCart(fresh); })
          .catch(function () {});
      }

      var prev = cartSnapshot || {};
      var removed = [];
      Object.keys(prev).forEach(function (key) {
        var beforeQty = prev[key].quantity;
        var now = cart.items.filter(function (i) { return i.key === key; })[0];
        var afterQty = now ? now.quantity : 0;
        var delta = beforeQty - afterQty;
        if (delta > 0) {
          var ga4 = itemFromCartLine(prev[key].item, prev[key].index);
          ga4.quantity = delta;
          removed.push(ga4);
        }
      });
      cartSnapshot = snapshotFromCart(cart);
      if (removed.length) {
        push('remove_from_cart', {
          currency: cart.currency,
          value: sumValue(removed),
          items: removed
        });
      }
    });
  }

  /* ---------------------------------------------------------------------- *
   * view_cart — when the cart drawer opens                                 *
   * ---------------------------------------------------------------------- */
  function fireViewCartFromServer() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart.items || []).map(itemFromCartLine);
        push('view_cart', {
          currency: cart.currency,
          value: money(cart.total_price),
          items: items
        });
      })
      .catch(function () {});
  }

  function wrapDrawerOpen(attempt) {
    var M = window.Woolman && window.Woolman.ModalsAndDrawers;
    if (!M || typeof M.showModalOrDrawer !== 'function') {
      // global.js is deferred too; retry a few times in case it loads later.
      if ((attempt || 0) < 20) {
        setTimeout(function () { wrapDrawerOpen((attempt || 0) + 1); }, 250);
      }
      return;
    }
    if (M._skGa4Wrapped) return;
    M._skGa4Wrapped = true;

    var original = M.showModalOrDrawer;
    M.showModalOrDrawer = function (targetId) {
      var wasOpen = false;
      if (targetId === 'drawer-cart') {
        var el = document.getElementById(targetId);
        wasOpen = !!(el && el.classList.contains('is-open'));
      }
      var result = original.apply(this, arguments);
      if (targetId === 'drawer-cart' && !wasOpen) fireViewCartFromServer();
      return result;
    };
  }

  /* ---------------------------------------------------------------------- *
   * view_item_list / select_item                                           *
   *                                                                        *
   * Lists are registered from JS (machine-search via registerItemList) or  *
   * scanned from inert JSON holders that Liquid renders inside the grid so  *
   * they survive AJAX pagination. We index items by their URL pathname so   *
   * a delegated click on any product card link can fire select_item.       *
   * ---------------------------------------------------------------------- */
  var itemsByPath = {}; // pathname -> { item, list_id, list_name }

  function normalizePath(href) {
    try {
      return new URL(href, window.location.origin).pathname.replace(/\/$/, '');
    } catch (e) {
      return (href || '').split('?')[0].split('#')[0].replace(/\/$/, '');
    }
  }

  function registerItemList(list) {
    if (!list || !Array.isArray(list.items)) return;
    list.items.forEach(function (item) {
      if (!item || !item.url) return;
      itemsByPath[normalizePath(item.url)] = {
        item: item,
        list_id: list.list_id,
        list_name: list.list_name
      };
    });
  }

  function viewItemList(list) {
    if (!list || !Array.isArray(list.items) || !list.items.length) return;
    var ecommerce = {
      item_list_id: list.list_id,
      item_list_name: list.list_name,
      items: list.items.map(function (item, index) {
        var copy = {};
        for (var k in item) { if (item.hasOwnProperty(k) && k !== 'url') copy[k] = item[k]; }
        copy.index = index;
        copy.item_list_id = list.list_id;
        copy.item_list_name = list.list_name;
        return copy;
      })
    };
    if (list.currency) ecommerce.currency = list.currency;
    push('view_item_list', ecommerce);
  }

  // Collection grids emit their list as an inert <script type="application/json"
  // class="sk-ga4-item-list"> inside #product-grid-ajax, so the data travels
  // with AJAX-paginated fragments. Scan for any not-yet-processed holders
  // (root defaults to the document; pass the appended fragment on pagination).
  function scanItemLists(root) {
    root = root || document;
    if (!root.querySelectorAll) return;
    var holders = root.querySelectorAll('script.sk-ga4-item-list:not([data-sk-ga4-done])');
    Array.prototype.forEach.call(holders, function (holder) {
      holder.setAttribute('data-sk-ga4-done', '1');
      var items;
      try { items = JSON.parse(holder.textContent || '[]'); } catch (e) { return; }
      if (!Array.isArray(items) || !items.length) return;
      var list = {
        list_id: holder.getAttribute('data-list-id') || '',
        list_name: holder.getAttribute('data-list-name') || '',
        currency: holder.getAttribute('data-currency') || undefined,
        items: items
      };
      registerItemList(list);
      viewItemList(list);
    });
  }

  function handleSelectItemClick(e) {
    var link = e.target.closest && e.target.closest('.card--product a[href]');
    if (!link) return;
    var entry = itemsByPath[normalizePath(link.getAttribute('href'))];
    if (!entry) return;
    var item = {};
    for (var k in entry.item) { if (entry.item.hasOwnProperty(k) && k !== 'url') item[k] = entry.item[k]; }
    item.item_list_id = entry.list_id;
    item.item_list_name = entry.list_name;
    push('select_item', {
      item_list_id: entry.list_id,
      item_list_name: entry.list_name,
      items: [item]
    });
  }

  // The machine-search results script lives in the page body and can run
  // before this deferred file, so it pushes its term to SK_GA4_SEARCH. Drain
  // any buffered terms on init, then replace the queue's push so later terms
  // fire immediately — mirrors how scanItemLists handles late item lists.
  function drainSearchQueue() {
    var queue = window.SK_GA4_SEARCH || [];
    if (Array.isArray(queue)) queue.forEach(function (term) { SKAnalytics.search(term); });
    window.SK_GA4_SEARCH = { push: function (term) { SKAnalytics.search(term); } };
  }

  /* ---------------------------------------------------------------------- *
   * Public API                                                             *
   * ---------------------------------------------------------------------- */
  var SKAnalytics = {
    push: push,
    money: money,
    itemFromCartLine: itemFromCartLine,
    registerItemList: registerItemList,
    viewItemList: viewItemList,
    scanItemLists: scanItemLists,

    search: function (term, resultsCount) {
      var payload = { search_term: term || '' };
      if (typeof resultsCount === 'number') payload.results_count = resultsCount;
      try {
        dataLayer().push({ event: 'search', search: payload, search_term: term || '' });
      } catch (e) {}
    },

    generateLead: function (detail) {
      detail = detail || {};
      try {
        dataLayer().push({
          event: 'generate_lead',
          lead_source: detail.lead_source || undefined,
          form_id: detail.form_id || undefined,
          currency: detail.currency || undefined,
          value: detail.value || 0
        });
      } catch (e) {}
    },

    // Called by assets/quote.js when a quote is created. `cart` is the
    // /cart.js snapshot captured before the cart is cleared.
    purchase: function (cart, reference) {
      try {
        if (!cart || !Array.isArray(cart.items)) return;
        push('purchase', {
          transaction_id: reference,
          currency: cart.currency,
          value: money(cart.total_price),
          items: cart.items.map(itemFromCartLine)
        });
      } catch (e) { /* analytics must never break the page */ }
    }
  };

  window.SKAnalytics = SKAnalytics;

  function init() {
    seedCart();
    scanItemLists(document);
    drainSearchQueue();
    wrapDrawerOpen();
    document.addEventListener('ajaxProduct:added', handleAdded);
    document.addEventListener('cart:update', handleCartUpdate);
    document.addEventListener('click', handleSelectItemClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
